import { useEffect, useRef, useMemo, useState } from "react"
import {
  useLocation,
  useNavigate,
  useOutletContext,
  useSearchParams,
} from "react-router-dom"

import moment from "moment"
import nProgress from "nprogress"
import { Menu } from "@headlessui/react"
import { useAuth } from "contexts/auth-context"
import { Pagination } from "components"
import { lang } from "config"
import { usePage } from "contexts/page-context"
import Table from "components/table"

import Import from "pages/product/stock/import"

import useProductUnitStockRepositories from "repositories/product-unit-stock"
import useBranchRepositories from "repositories/branch"
import { CloudDownload } from "components/icons/outline"
import SelectDescription from "components/forms/select-description"
import useDebouncedSearchParam from "hooks/use-debounced-search-param"
import { getBranchList, getCurrentEntity } from "helpers/general-helper"
import { defaultUomForEntity, loadMasterUomFactors, masterUomFactor } from "helpers/uom-helper"

// 270726 (permintaan user): pilihan SATUAN di halaman stok — stok tersimpan PCS,
// selector hanya mengubah cara tampil (qty ÷ faktor satuan). Opsi diambil dari
// MASTER satuan (GET /satuan) seperti form pembelian/retur; konstanta di bawah
// hanya fallback saat master belum termuat.
const UOM_FALLBACK = ["PCS", "LUSIN", "PASANG"]

export default function List() {
  // Logged in user ability
  const { can, isSuper, currentBranch } = useAuth()

  // Configuration (kalau dibutuhkan)
  const { configuration } = usePage()

  // Transition
  const { title, transition, setTransition } = useOutletContext()

  // React router navigate hook
  const navigate = useNavigate()

  // React router location hook
  const location = useLocation()

  // Query parameters
  const [searchParams, setSearchParams] = useSearchParams()

  const searchInput = useRef()

  const page = searchParams.get("page")
  const search = searchParams.get("search")
  const [searchTemp, setSearchTemp, submitSearch] = useDebouncedSearchParam({ search, searchParams, setSearchParams })
  const outlet_id = searchParams.get("outlet_id")
  const only_in_stock = searchParams.get("only_in_stock")

  // Satuan tampilan stok (tidak dikirim ke server — murni presentasi).
  const stockUom = (searchParams.get("uom") || "PCS").toUpperCase()
  const [satuanOptions, setSatuanOptions] = useState(null)
  useEffect(() => {
    let cancelled = false
    loadMasterUomFactors().then((names) => {
      if (cancelled) return
      const opts = names.length ? names : [...UOM_FALLBACK]
      setSatuanOptions(opts)
      // 290726: satuan default ikut ENTITAS user (VISION→PASANG, ATALLA→LUSIN).
      // Hanya saat user belum memilih sendiri (param `uom` kosong di URL).
      if (!searchParams.get("uom")) {
        const def = defaultUomForEntity(getCurrentEntity(), opts)
        if (def && def !== "PCS") {
          setSearchParams((prev) => {
            const next = new URLSearchParams(prev)
            next.set("uom", def)
            return next
          }, { replace: true })
        }
      }
    })
    return () => { cancelled = true }
  }, [])
  const stockFactor = masterUomFactor(stockUom)

  const orderBy = searchParams.get("order_by")
  const direction = searchParams.get("direction")

  // ATALLA-408: origin outlet untuk sort "jarak" = outlet aktif user (fallback outlet filter).
  const originOutletId = currentBranch?.id ? String(currentBranch.id) : outlet_id

  // ATALLA-452: origin jarak yang BENAR = lokasi FISIK user (GPS browser), bukan outlet
  // aktif (mis. HO PT Atalla di Jakarta yang jadi "terdekat" untuk user di Bogor). Minta
  // geolokasi hanya saat sort jarak; fail-soft (ditolak/tak didukung → backend fallback ke
  // origin_outlet_id, perilaku lama).
  const [userGeo, setUserGeo] = useState(null)
  useEffect(() => {
    if (orderBy !== "distance" || userGeo || typeof navigator === "undefined" || !navigator.geolocation) return
    navigator.geolocation.getCurrentPosition(
      (pos) => setUserGeo({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
      () => {},
      { enableHighAccuracy: false, timeout: 8000, maximumAge: 300000 }
    )
  }, [orderBy, userGeo])

  const {
    data: productUnitStockData,
    isLoading: isLoadingProductUnitStockData,
    mutate: mutateProductUnitStockData,
  } = useProductUnitStockRepositories({
    page,
    search,
    outlet_id,
    only_in_stock,
    order_by: orderBy,
    direction,
    // ATALLA-408: dikirim hanya saat sort jarak agar cache SWR sort lain tak berubah.
    origin_outlet_id: orderBy === "distance" ? originOutletId : undefined,
    // ATALLA-452: koordinat GPS user (prioritas di backend); fallback ke origin_outlet_id.
    origin_lat: orderBy === "distance" ? userGeo?.lat : undefined,
    origin_lng: orderBy === "distance" ? userGeo?.lng : undefined,
    aggregate: true,
  })

  // 230726 (rule entity, revisi ATALLA-389/405): Product Stock terbuka — semua user
  // (yang punya akses halaman via permission) bisa melihat & memfilter outlet mana
  // pun dari entitas mana pun; node cross-outlet tidak dipakai lagi di halaman ini.

  // Label nama outlet: ambil DAFTAR LENGKAP outlet (unrestricted) HANYA untuk resolusi
  // nama pada kolom Outlet. Ini aman: backend (ATALLA-405) yang menentukan baris outlet
  // mana yang boleh tampil; di sini kita cuma memetakan id->nama supaya tidak muncul id
  // mentah untuk outlet milik user yang bukan outlet aktifnya.
  const { data: allOutlets, isLoading: isLoadingOutletSelection } =
    useBranchRepositories({ paginate: false, unrestricted: true })

  const ownOutlets = useMemo(() => {
    const list = getBranchList()
    return Array.isArray(list) ? list.filter((b) => b && b.id) : []
  }, [])
  // 230726 (rule entity, revisi ATALLA-408/405): Product Stock DIBUKA — keputusan
  // user: setiap user bisa melihat outlet mana pun dari entitas mana pun; opsi
  // filter = SEMUA outlet (backend juga sudah tidak membatasi).
  // 270726 (permintaan user): opsi filter outlet diurutkan ASC by id outlet.
  const filterOutlets = useMemo(() => {
    const list = (allOutlets || []).length ? allOutlets : ownOutlets
    return [...(list || [])].sort((a, b) => Number(a?.id ?? 0) - Number(b?.id ?? 0))
  }, [allOutlets, ownOutlets])

  const outletById = useMemo(() => {
    const map = new Map()
    // gabungkan daftar lengkap + outlet milik user supaya semua id ter-resolve namanya
    ;[...(allOutlets || []), ...ownOutlets].forEach((o) => {
      // key string supaya cocok dengan outlet_id dari product_unit_stock (string di DB)
      map.set(String(o.id), o)
    })
    return map
  }, [allOutlets, ownOutlets])

  // ATALLA-408: kontrol sort ringkas (Qty / Jarak) selain sort via header kolom.
  const setSort = (value) => {
    if (!value) {
      mergeParams({ order_by: null, direction: null, page: 1 })
    } else if (value === "distance") {
      mergeParams({ order_by: "distance", direction: "asc", page: 1 })
    } else if (value === "qty") {
      mergeParams({ order_by: "current_stock", direction: "desc", page: 1 })
    }
  }
  const currentSort =
    orderBy === "distance" ? "distance" : orderBy === "current_stock" ? "qty" : ""

  const mergeParams = (next) => {
    const current = Object.fromEntries(searchParams.entries())
    // bersihkan null/undefined/""
    const merged = { ...current, ...next }
    Object.keys(merged).forEach((k) => {
      if (merged[k] === null || merged[k] === undefined || merged[k] === "") {
        delete merged[k]
      }
    })
    setSearchParams(merged)
  }

  useEffect(() => {
    window.history.replaceState({}, document.title)

    if (transition?.reverse) {
      setTransition({ type: "slide", reverse: false })
    }
  }, [])

  useEffect(() => {
    if (searchInput.current) {
      searchInput.current.focus()
    }
  }, [search])

  useEffect(() => {
    if (search === "") {
      navigate("", { replace: true })
    }

    if (isLoadingProductUnitStockData) {
      nProgress.start()
    } else {
      nProgress.done()
    }
  }, [productUnitStockData, isLoadingProductUnitStockData])

  // Data sort handlers
  const toggleSort = (value) => {
    if ((direction === null || direction === "desc") || orderBy !== value) {
      mergeParams({ order_by: value, direction: "asc", page: 1 })
    } else {
      mergeParams({ order_by: value, direction: "desc", page: 1 })
    }
  }

  // Headers tabel
  const dataListHeaders = [
    {
      isAction: false,
      content: "ID Produk",
      sortKey: "product_unit_id",
    },
    {
      isAction: false,
      content: "Nama Produk",
      sortKey: "product_unit_id",
    },
    {
      isAction: false,
      content: "Outlet",
      sortKey: "outlet_id",
    },
    {
      isAction: false,
      content: `Total Stock (${stockUom})`,
      sortKey: "current_stock",
    },
    {
      isAction: false,
      content: "Diubah Pada",
      sortKey: "updated_at",
    },
    {
      isAction: true,
      content: null,
    },
  ]

  // Kolom tabel
  const dataListColumns = [
    // Product ID
    {
      type: "default",
      bold: true,
      content: (value) => value.product_unit_id ?? "-",
    },
    // Nama Produk
    {
      type: "default",
      bold: true,
      content: (value) =>
        value.product_unit_name ||
        value.product_unit?.name ||
        value.productUnit?.name ||
        "-",
    },
    // Outlet
    {
      type: "default",
      bold: false,
      content: (value) => {
        const outlet = outletById.get(String(value.outlet_id))
        return outlet?.name ?? value.outlet?.name ?? value.outlet_name ?? value.outlet_id ?? "-"
      },
    },
    // Total Stock (sum actual_stock per product_unit_id + outlet_id)
    {
      type: "default",
      bold: true,
      content: (value) => {
        // Stok tersimpan PCS → tampilkan per satuan terpilih (2 desimal bila pecahan).
        const pcs = Number(value.actual_stock ?? value.current_stock ?? 0)
        const converted = stockFactor > 1 ? pcs / stockFactor : pcs
        return converted.toLocaleString("id-ID", { maximumFractionDigits: 2 })
      },
    },
    // Updated at
    {
      type: "default",
      bold: false,
      content: (value) =>
        value.updated_at
          ? moment(value.updated_at).format("MMMM D, YYYY")
          : "-",
    },
    // Action
    {
      type: "action",
      content: () => <></>,
    },
  ]

  const data = productUnitStockData?.data ?? []
  const isEmpty = (data?.length ?? 0) === 0 && !search && !isLoadingProductUnitStockData
  const isEmptyOnSearching =
    (data?.length ?? 0) === 0 && !!search && !isLoadingProductUnitStockData

  const hasExportPermission = isSuper() || can('product-stock.export')
  const canExport = hasExportPermission && !!outlet_id

  return (
    <div className="space-y-6">
      {/* Header: search + filter + export/import */}
      <div className="flex items-center justify-between text-xs">
        <div className="flex items-center sm:space-x-2">
          {/* Search box */}
          <div className="relative hidden sm:block">
            <div className="absolute inset-y-0 flex items-center pl-3 pointer-events-none">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="w-4 h-4"
                width={24}
                height={24}
                viewBox="0 0 24 24"
                strokeWidth={1}
                stroke="currentColor"
                fill="none"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path stroke="none" d="M0 0h24v24H0z" fill="none"></path>
                <circle cx={10} cy={10} r={7}></circle>
                <line x1={21} y1={21} x2={15} y2={15}></line>
              </svg>
            </div>

            <input
              ref={searchInput}
              onChange={(e) => setSearchTemp(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); submitSearch() } }}
              value={searchTemp}
              type="text"
              placeholder={`${lang.search} Stock Product Unit`}
              autoComplete="off"
              className="w-64 py-3 pl-8 pr-4 text-xs transition border border-neutral-200 focus:outline-none rounded-xl focus:border-neutral-400 focus:ring focus:ring-neutral-200"
            />
          </div>

          {/* Import */}
          {can('product-stock.import') && <Import onSuccess={() => mutateProductUnitStockData()} />}
        </div>

        {/* Right side: filter outlet + sort (selalu tampil) + ekspor (gated) */}
        <div className="flex items-center space-x-2">

          {/* ATALLA-408: Filter Outlet — opsi dibatasi permission (cross-outlet → semua
              outlet; selain itu → outlet milik user, backend juga menegakkan ATALLA-405). */}
          <div className="w-56">
            <SelectDescription
              onChange={(value) => mergeParams({ outlet_id: value, page: 1 })}
              selection={filterOutlets || []}
              isLoading={isLoadingOutletSelection}
              value={outlet_id}
              keyValue={(value) => value.id}
              title={(value) => value.name}
              description={(value) => value.code || value.id}
              placeholder="Pilih Outlet"
            />
          </div>

          {/* 270726 (permintaan user): pilihan SATUAN tampilan stok. Nilai tersimpan
              tetap PCS — selector hanya membagi tampilan dengan faktor satuan. */}
          <select
            value={stockUom}
            onChange={(e) => mergeParams({ uom: e.target.value, page: 1 })}
            className="py-3 px-3 text-xs transition bg-white border border-neutral-200 focus:outline-none rounded-xl focus:border-neutral-400 focus:ring focus:ring-neutral-200"
            title="Satuan tampilan stok"
          >
            {((satuanOptions ?? UOM_FALLBACK).includes(stockUom)
              ? (satuanOptions ?? UOM_FALLBACK)
              : [stockUom, ...(satuanOptions ?? UOM_FALLBACK)]
            ).map((u) => (
              <option key={u} value={u}>Satuan: {u}</option>
            ))}
          </select>

          {/* ATALLA-408: Urutkan berdasarkan Qty (stok terbanyak) / Jarak (outlet terdekat). */}
          <select
            value={currentSort}
            onChange={(e) => setSort(e.target.value)}
            className="py-3 px-3 text-xs transition bg-white border border-neutral-200 focus:outline-none rounded-xl focus:border-neutral-400 focus:ring focus:ring-neutral-200"
            title="Urutkan hasil"
          >
            <option value="">Urutkan: Default</option>
            <option value="qty">Urutkan: Qty (terbanyak)</option>
            <option value="distance">Urutkan: Jarak (terdekat)</option>
          </select>

          {can('product-stock.export') && (
            <Menu as="div" className="relative inline-block text-left">
              <Menu.Button
                disabled={!canExport}
                className={[
                  "inline-flex items-center px-4 py-3 space-x-2 text-xs transition border border-neutral-200 text-white bg-brand-800 rounded-xl active:hover:scale-90",
                  !canExport ? "opacity-50 cursor-not-allowed" : "",
                ].join(" ")}
                title={
                  !hasExportPermission
                    ? "Tidak dapat ekspor"
                    : !outlet_id
                      ? "Pilih outlet dulu pada filter untuk ekspor"
                      : "Ekspor Excel"
                }
              >
                <CloudDownload className="w-4 h-4" stroke={1.5} />
                <span>Ekspor Excel</span>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="w-3.5 h-3.5"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth={2}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="m6 9 6 6 6-6" />
                </svg>
              </Menu.Button>

              <Menu.Items className="absolute right-0 z-30 mt-2 w-56 overflow-hidden origin-top-right rounded-xl border border-neutral-200 bg-white shadow-xl ring-1 ring-black/5 focus:outline-none">
                <div className="p-1">
                  <Menu.Item>
                    {({ active }) => (
                      <button
                        type="button"
                        onClick={() => {
                          const url = `${process.env.REACT_APP_INVENTORY_API_URL}/product-unit-stocks/export?type=stock&outlet_id=${encodeURIComponent(
                            outlet_id ?? ""
                          )}`
                          window.open(url, "_blank")
                        }}
                        className={`flex w-full flex-col items-start rounded-lg px-3 py-2.5 text-left transition ${
                          active ? "bg-neutral-100" : "bg-white"
                        }`}
                      >
                        <span className="text-xs font-semibold text-neutral-800">Ekspor Stock</span>
                        <span className="text-[10px] text-neutral-500">Qty per product unit</span>
                      </button>
                    )}
                  </Menu.Item>
                  <Menu.Item>
                    {({ active }) => (
                      <button
                        type="button"
                        onClick={() => {
                          const url = `${process.env.REACT_APP_INVENTORY_API_URL}/product-unit-stocks/export?type=value&outlet_id=${encodeURIComponent(
                            outlet_id ?? ""
                          )}`
                          window.open(url, "_blank")
                        }}
                        className={`flex w-full flex-col items-start rounded-lg px-3 py-2.5 text-left transition ${
                          active ? "bg-neutral-100" : "bg-white"
                        }`}
                      >
                        <span className="text-xs font-semibold text-neutral-800">Ekspor Nilai Stock</span>
                        <span className="text-[10px] text-neutral-500">Total nilai (qty × HPP)</span>
                      </button>
                    )}
                  </Menu.Item>
                </div>
              </Menu.Items>
            </Menu>
          )}
        </div>
      </div>

      {/* Tabel list */}
      <Table
        isLoading={isLoadingProductUnitStockData || isLoadingOutletSelection}
        data={data}
        headers={dataListHeaders}
        columns={dataListColumns}
        key={(value) =>
          `${value.product_unit_id}-${value.outlet_id}`
        }
        isEmpty={isEmpty}
        isEmptyOnSearching={isEmptyOnSearching}
        toggleSort={toggleSort}
        orderBy={orderBy}
        direction={direction}
      />

      {/* Pagination */}
      <Pagination
        links={productUnitStockData?.links}
        from={productUnitStockData?.from}
        to={productUnitStockData?.to}
        total={productUnitStockData?.total}
      />
    </div>
  )
}
